import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import MedicineCatalogService from "./MedicineCatalogService";

/**
 * Debounced fuzzy-search dropdown for the medicine catalog. Same shape as
 * chiefComplaints/IcdAutocomplete but wired to api-medicine-service.
 *
 * On pick, hands the parent a snapshot object with just the fields the
 * backend stores as `medicineCode` — the AddMedicineModal writes it into the
 * request body and also auto-fills the free-text display + formulation +
 * unit from it.
 */
const DEBOUNCE_MS = 300;
// Minimum characters before firing a search. 1-char prefixes match
// most of the catalog and were the dominant source of Mongo scan load
// on the ranked-search path — reviewers always type more than one
// letter anyway. Both the typed-input path (runSearch) and the mount-
// time prefetch below respect this floor.
const MIN_CHARS = 2;

// De-dup by mCode. The server usually returns one row per mCode but the
// contract doesn't guarantee it.
const dedupeByMCode = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = (r && r.mCode) || (r && r.id) || "";
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

const MedicineCatalogAutocomplete = forwardRef(function MedicineCatalogAutocomplete(
  {
    value,
    mCode,
    onChange,
    disabled,
    isBlocked,
    onBlockedPick,
    isAlreadyActive,
  },
  ref
) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const serviceRef = useRef(null);
  // Monotonic request token — bumped on every keystroke AND every
  // clear, so a slow in-flight response from a prior query can't race
  // ahead of the current one and overwrite the dropdown. Callback
  // captures the token at request time; we only apply the response
  // when it still matches the latest token.
  const seqRef = useRef(0);

  // Expose a focus() handle so the parent form can return focus to the
  // search field after dismissing the "cannot prescribe" dialog.
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) inputRef.current.focus();
    },
  }));

  if (!serviceRef.current) serviceRef.current = new MedicineCatalogService();

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Shared search primitive. `openWhenDone` controls whether the
  // dropdown auto-opens on response (typing + focus paths pass true;
  // the mount-time prefetch below passes false so the results are
  // ready to render on the first focus, without popping the menu
  // open before the reviewer has touched the field). Every call
  // bumps the seq token so stale responses can't clobber fresher ones.
  const runSearch = (q, { openWhenDone = true, immediate = false } = {}) => {
    const mySeq = ++seqRef.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = (q || "").trim();
    if (!trimmed) {
      setResults([]);
      if (openWhenDone) setOpen(false);
      setLoading(false);
      return;
    }
    // Under-min: keep the menu open so the "Type at least N characters"
    // hint row renders — closing silently reads as a broken dropdown.
    // Mirrors IcdAutocomplete (CC) which already uses this pattern.
    if (trimmed.length < MIN_CHARS) {
      setResults([]);
      if (openWhenDone) setOpen(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const fire = () => {
      serviceRef.current.search(
        q,
        (data) => {
          if (mySeq !== seqRef.current) return;
          setResults(dedupeByMCode(Array.isArray(data) ? data : []));
          if (openWhenDone) setOpen(true);
          setLoading(false);
        },
        () => {
          if (mySeq !== seqRef.current) return;
          setResults([]);
          setLoading(false);
        }
      );
    };
    if (immediate) fire();
    else debounceRef.current = setTimeout(fire, DEBOUNCE_MS);
  };

  // Mount-time prefetch: when the parent seeds a `value` (typically
  // the transcript flow's AI-populated medicine name), warm the
  // results so the FIRST focus shows the dropdown immediately
  // instead of a blank menu. Fires once per mount; a later external
  // `value` change (e.g. a pick sync) doesn't re-trigger.
  //
  // Also auto-picks when the AI's string is an exact (case-insensitive)
  // match to one catalog row — the reviewer never has to click. Skip
  // when the parent already handed us an mCode (a downstream click-to-
  // edit already resolved the pick), and skip when the exact match is
  // blocked (allergy / already-active) so we don't silently commit to
  // a dead-end row.
  const didInitialSearch = useRef(false);
  useEffect(() => {
    if (didInitialSearch.current) return;
    const seed = (value || "").trim();
    if (seed.length < MIN_CHARS) return;
    if (mCode) return;
    didInitialSearch.current = true;
    const mySeq = ++seqRef.current;
    setLoading(true);
    serviceRef.current.search(
      seed,
      (data) => {
        if (mySeq !== seqRef.current) return;
        const rows = dedupeByMCode(Array.isArray(data) ? data : []);
        setResults(rows);
        setLoading(false);
        const exact = rows.find(
          (r) => (r.medicineName || "").trim().toLowerCase() === seed.toLowerCase()
        );
        if (!exact) return;
        const blockedBy = isBlocked ? isBlocked(exact) : null;
        const alreadyActive = !blockedBy && isAlreadyActive
          ? !!isAlreadyActive(exact)
          : false;
        if (blockedBy || alreadyActive) return;
        pick(exact);
      },
      () => {
        if (mySeq !== seqRef.current) return;
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Free-text edit clears the catalog link — the parent's medicineCode is
    // dropped so the backend treats this as a fresh free-text entry (no dedup).
    onChange({
      medicine: q,
      medicineCode: null,
      formulation: "",
      formulationName: "",
    });
    runSearch(q);
  };

  const pick = (item) => {
    const display = item.medicineName || "";
    setQuery(display);
    setOpen(false);
    // Both `formulation` (raw REFLD code or legacy plain label) and
    // `formulationName` (resolved human label from api-medicine-service's
    // MEDICINE_FORMULATIONS lookup) are snapshotted onto the catalog copy
    // AND surfaced at the top level so the modal + display path can pick
    // either without another lookup. formulationName may be "" when the
    // reference-list resolver hits an unknown code — fall back to
    // `formulation` at render time.
    onChange({
      medicine: display,
      medicineCode: {
        id: item.id || "",
        mCode: item.mCode || "",
        medicineName: item.medicineName || "",
        manufacturerName: item.manufacturerName || "",
        formulation: item.formulation || "",
        formulationName: item.formulationName || "",
      },
      formulation: item.formulation || "",
      formulationName: item.formulationName || "",
    });
  };

  return (
    <div className="cc-icd" ref={boxRef}>
      <div className="sec-search">
        <svg
          className="sec-search-icon"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleType}
          onFocus={() => {
            const q = (query || "").trim();
            if (!q) return;
            // Always open when the field carries content — the menu
            // renders the "Searching…" / result rows / "No matches"
            // state so the reviewer never sees a mysteriously empty
            // click. Fire a search if we don't have results yet
            // (covers the AI-populated-then-first-click case).
            setOpen(true);
            if (results.length === 0 && !loading) {
              runSearch(q, { immediate: true });
            }
          }}
          placeholder="Search a medicine"
          disabled={disabled}
        />
      </div>
      {open && (
        <ul className="cc-icd-menu" role="listbox">
          {loading && <li className="cc-icd-empty">Searching…</li>}
          {!loading && results.length === 0 && query.trim().length < MIN_CHARS && (
            <li className="cc-icd-empty">
              Type at least {MIN_CHARS} characters to search.
            </li>
          )}
          {!loading && results.length === 0 && query.trim().length >= MIN_CHARS && (
            <li className="cc-icd-empty">
              No matches — refine the search to pick from the list.
            </li>
          )}
          {!loading &&
            results.map((r) => {
              // Two disable reasons, in priority order:
              //   1. Salt-allergy block (allergy tag; opens "cannot
              //      prescribe" dialog on click via onBlockedPick).
              //   2. Already-active on the patient (already-added tag;
              //      click is a no-op — parent instructs to Update instead).
              const blockedBy = isBlocked ? isBlocked(r) : null;
              const blockingAllergyName =
                blockedBy && blockedBy.allergy && blockedBy.allergy.name;
              const alreadyActive = !blockedBy && isAlreadyActive
                ? !!isAlreadyActive(r)
                : false;
              const disabledClass = blockedBy
                ? " is-blocked"
                : alreadyActive
                  ? " is-already-active"
                  : "";
              return (
                <li
                  key={r.mCode || r.id}
                  role="option"
                  aria-selected={mCode && r.mCode === mCode}
                  aria-disabled={!!blockedBy || alreadyActive}
                  className={`cc-icd-opt${disabledClass}`}
                  onClick={() => {
                    if (blockedBy) {
                      // Close the dropdown too — the "cannot prescribe"
                      // dialog is a full stop, the menu shouldn't linger
                      // behind it inviting a second click.
                      setOpen(false);
                      if (onBlockedPick) onBlockedPick(blockedBy, r);
                      return;
                    }
                    if (alreadyActive) {
                      // Silent no-op — the visible tag + tooltip already
                      // tell the reviewer why. Update sheet is the right
                      // route; nothing to do on a picker click.
                      return;
                    }
                    pick(r);
                  }}
                  title={
                    blockedBy
                      ? `Blocked — contains ${blockingAllergyName} (recorded salt allergy)`
                      : alreadyActive
                        ? "Already in the active medicines list — use its Update sheet to change status"
                        : undefined
                  }
                >
                  {/* Board 2964:4704 — the name stands alone and the
                      FORMULATION is the chip (a white bordered pill led by
                      the 6px orange dot). The mCode is an internal
                      identifier the board never draws. Right-side chips
                      stack in `.cc-icd-side` so the state pill (already
                      added / allergy) drops UNDER the formulation chip
                      instead of running horizontally past it. */}
                  <span className="cc-icd-name">{r.medicineName}</span>
                  <span className="cc-icd-side">
                    {(r.formulationName || r.formulation) && (
                      <span className="cc-icd-tag">
                        <span className="med3-ac-dot" aria-hidden="true" />
                        {r.formulationName || r.formulation}
                      </span>
                    )}
                    {blockedBy && (
                      <span className="cc-icd-blocked-tag" aria-hidden="true">
                        allergy
                      </span>
                    )}
                    {alreadyActive && (
                      <span className="cc-icd-active-tag" aria-hidden="true">
                        already added
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
});

MedicineCatalogAutocomplete.propTypes = {
  value: PropTypes.string,
  mCode: PropTypes.string, // currently-picked mCode
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** (item) → matched allergy, or null. Grays out the row + intercepts click. */
  isBlocked: PropTypes.func,
  /** Fired when a clinician clicks a grayed row. Receives (allergy, item). */
  onBlockedPick: PropTypes.func,
  /** (item) → true when this medicine is already in the patient's active
   *  list. Row is highlighted with an "already added" tag and click is a no-op. */
  isAlreadyActive: PropTypes.func,
};

export default MedicineCatalogAutocomplete;
